import { type UseMutationOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiResponseError } from '../../../types/errors';
import { PlatformCustomerError } from '../errors';
import type { CreatePlatformCustomerPayload } from '../types/api';
import type { Customer } from '../types/customer';
import { useCustomersApi } from './use-customers-api';
import { CUSTOMER_LOOKUP, QK_GET_CUSTOMER } from './use-get-customer';

type UseCreatePlatformCustomerOptions = Partial<
  Omit<
    UseMutationOptions<Customer, PlatformCustomerError, CreatePlatformCustomerPayload>,
    'mutationFn'
  >
>;

// Maps transport failures to a typed PlatformCustomerError so consumers don't touch HTTP status
// codes, and primes the customer caches from the response.
export function useCreatePlatformCustomer(options: UseCreatePlatformCustomerOptions = {}) {
  const queryClient = useQueryClient();
  const { customersApi } = useCustomersApi();

  return useMutation<Customer, PlatformCustomerError, CreatePlatformCustomerPayload>({
    ...options,
    mutationFn: async (payload) => {
      try {
        return await customersApi.platformCustomer().create(payload);
      } catch (err) {
        if (err instanceof ApiResponseError && err.status === 409) {
          throw new PlatformCustomerError(PlatformCustomerError.Id.AlreadyExists);
        }
        throw new PlatformCustomerError(PlatformCustomerError.Id.CreateFailed);
      }
    },
    onSuccess: (customer, ...rest) => {
      void queryClient.invalidateQueries({
        queryKey: ['customers'],
      });
      queryClient.setQueryData(QK_GET_CUSTOMER(CUSTOMER_LOOKUP.email(customer.email)), customer);
      queryClient.setQueryData(QK_GET_CUSTOMER(CUSTOMER_LOOKUP.sparklayer(customer.id)), customer);
      if (customer.external_id) {
        queryClient.setQueryData(
          QK_GET_CUSTOMER(CUSTOMER_LOOKUP.platform(customer.external_id)),
          customer,
        );
      }
      options.onSuccess?.(customer, ...rest);
    },
  });
}
